import { CmdTooltip } from '@commander-services/gui-components';
import * as React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useIntl } from 'react-intl';
import './VehicleCluster.scss';
import IconService from '../../../Services/IconService';
import { isVehicleGdprDataAllowedAtom } from '../../../store/recoil/vehicles';
import { IVehicleClusterTooltipItem } from './interfaces';
import { IVehicleGdprDataAllowed, TVehicleStatus } from '../../Vehicles/types';
import { isBluecoinsOpenAtom } from '../Bluecoins/BluecoinState';
import { isWaypointsOpenAtom } from '../Waypoints/WaypointState';
import { autofocusAtom } from '../OnlineMapState';
import {
  mapInfoWindowAdditionalAtom,
  mapInfoWindowAlarmsAtom,
  mapInfoWindowBluecoinsAtom,
  mapInfoWindowDoorsAtom,
  mapInfoWindowEventsAtom,
} from '../ToolbarLayer/VehicleChipsControl/InfoWindowState';
import useAnalytics from '../../../hooks/useAnalytics';

interface IVehicleClusterProps {
  clusterId: string;
  items: number;
  hasAlarm?: boolean;
  lat: number; // lat is used by OverlayView in GoogleMapsReactMarkers package
  lng: number; // lng is used by OverlayView in GoogleMapsReactMarkers package
  tooltipItems: IVehicleClusterTooltipItem[];
  expansionZoom: number;
  googleMap: google.maps.Map | undefined;
  hasEvent?: boolean;
  hasEventTypeDoor?: boolean;
  hasEventTypeAdditional?: boolean;
  hasEventTypeBluecoin?: boolean;
  isPrivate?: boolean;
  zIndex: number; // zIndex is used by OverlayView in GoogleMapsReactMarkers package
  handleOnHover: (value: string) => void;
}

export default function VehicleCluster(props: IVehicleClusterProps) {
  const { formatMessage: f } = useIntl();
  const setAutofocus = useSetRecoilState<boolean>(autofocusAtom);
  const isWaypointsOpen = useRecoilValue(isWaypointsOpenAtom);
  const showAlarms = useRecoilValue(mapInfoWindowAlarmsAtom);
  const showEvents = useRecoilValue(mapInfoWindowEventsAtom);
  const showDoors = useRecoilValue(mapInfoWindowDoorsAtom);
  const showAdditionalInfo = useRecoilValue(mapInfoWindowAdditionalAtom);
  const showBluecoin = useRecoilValue(mapInfoWindowBluecoinsAtom);
  const isVehicleGdprDataAllowed = useRecoilValue<IVehicleGdprDataAllowed>(
    isVehicleGdprDataAllowedAtom
  );
  const isBluecoinsOpen = useRecoilValue<boolean>(isBluecoinsOpenAtom);
  const { trackEvent } = useAnalytics();

  const handleOnClick = (_event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    if (props.googleMap) {
      trackEvent(['map', 'click', 'Click vehicle cluster on map']);
      props.googleMap.setZoom(props.expansionZoom);
      props.googleMap.panTo({ lat: props.lat, lng: props.lng });
      setAutofocus(false);
    }
  };

  const handleOnHover = () => {
    props.handleOnHover(props.clusterId);
  };

  const alarmIcon = (size?: string) => {
    const styles: React.CSSProperties = size ? { width: size } : {};
    return (
      <img src={new URL('/assets/map-alarm.svg', import.meta.url).href} alt="" style={styles} />
    );
  };

  const eventIcon = (size?: string) => {
    const styles: React.CSSProperties = size ? { width: size } : {};
    return <img src={new URL('/assets/events.svg', import.meta.url).href} alt="" style={styles} />;
  };

  const privateIcon = (vehicleStatus: TVehicleStatus, size?: string) => {
    const styles: React.CSSProperties = size ? { width: size } : {};
    return <img src={IconService.getIsPrivateIcon(vehicleStatus)} alt="" style={styles} />;
  };

  const showTooltipAlarmIcon = React.useCallback((): JSX.Element | null => {
    return alarmIcon('10px');
  }, []);

  const showTooltipPrivateIcon = React.useCallback((vehicleStatus: TVehicleStatus): JSX.Element => {
    return privateIcon(vehicleStatus, '10px');
  }, []);

  const showTooltipEventIcon = React.useCallback(
    (
      hasEvent?: boolean,
      hasEventTypeDoor?: boolean,
      hasEventTypeAdditional?: boolean,
      hasEventTypeBluecoin?: boolean
    ): JSX.Element | null => {
      if (
        (showEvents && showDoors && hasEvent && hasEventTypeDoor) ||
        (showEvents && showAdditionalInfo && hasEvent && hasEventTypeAdditional) ||
        (showEvents && showBluecoin && hasEvent && hasEventTypeBluecoin)
      ) {
        return eventIcon('10px');
      }
      return null;
    },
    [showAdditionalInfo, showBluecoin, showDoors, showEvents]
  );

  const showClusterAlarmIcon = React.useCallback((): JSX.Element | null => {
    if (props.hasAlarm && showAlarms) {
      return alarmIcon();
    }
    return null;
  }, [props.hasAlarm, showAlarms]);

  const showClusterPrivateIcon = React.useCallback(
    (vehicleStatus: TVehicleStatus): JSX.Element | null => {
      if (props.isPrivate) {
        return privateIcon(vehicleStatus);
      }
      return null;
    },
    [props.isPrivate]
  );

  const showClusterEventIcon = React.useCallback(
    (size?: string): JSX.Element | null => {
      if (
        (showEvents && showDoors && props.hasEvent && props.hasEventTypeDoor) ||
        (showEvents && showAdditionalInfo && props.hasEvent && props.hasEventTypeAdditional) ||
        (showEvents && showBluecoin && props.hasEvent && props.hasEventTypeBluecoin)
      ) {
        const styles: React.CSSProperties = size ? { width: size } : {};
        return (
          <img src={new URL('/assets/events.svg', import.meta.url).href} alt="" style={styles} />
        );
      }
      return null;
    },
    [
      props.hasEvent,
      props.hasEventTypeAdditional,
      props.hasEventTypeBluecoin,
      props.hasEventTypeDoor,
      showAdditionalInfo,
      showBluecoin,
      showDoors,
      showEvents,
    ]
  );

  const getVehiclesInCluster = React.useMemo(() => {
    const numberOfAllTooltipItems = props.tooltipItems.length;
    const allowedTooltipItems = props.tooltipItems.slice(0, 20);
    return (
      <div className="marker-cluster-toolitp" style={{ zIndex: props.zIndex, width: '250px' }}>
        <ul>
          {allowedTooltipItems.map((item: IVehicleClusterTooltipItem, index: number) => (
            <li className={`${item.status}-color`} key={`tooltip-item--${index}`}>
              <i className={`cmd-icon cmd-icon-circle ${item.status}-color`} />
              {item.licenceNumber} {item.hasAlarm && showAlarms && showTooltipAlarmIcon()}
              {showTooltipEventIcon(
                item.hasEvents,
                item.hasEventTypeDoor,
                item.hasEventTypeAdditional,
                item.hasEventTypeBluecoin
              )}{' '}
              {item.isPrivate &&
                isVehicleGdprDataAllowed[item.vehicleId] &&
                showTooltipPrivateIcon(item.status)}
            </li>
          ))}
        </ul>
        {numberOfAllTooltipItems > 20 && (
          <p>
            {f(
              { id: 'map.vehicleCluster.tooltip.moreItems' },
              { items: numberOfAllTooltipItems - allowedTooltipItems.length }
            )}
          </p>
        )}
      </div>
    );
  }, [
    f,
    isVehicleGdprDataAllowed,
    props.tooltipItems,
    props.zIndex,
    showAlarms,
    showTooltipAlarmIcon,
    showTooltipEventIcon,
    showTooltipPrivateIcon,
  ]);

  return (
    <div
      id={`cluster-${props.clusterId}`}
      onClick={handleOnClick}
      style={{
        cursor: 'pointer',
        zIndex: Number(props.zIndex),
        opacity: isWaypointsOpen || isBluecoinsOpen ? 0.5 : 1,
      }}
      onMouseEnter={handleOnHover}
    >
      <div className="cluster-icon-alarm" style={{ top: '-43px' }}>
        {showClusterPrivateIcon('DRIVING')}
        {showClusterAlarmIcon()}
        {showClusterEventIcon()}
      </div>
      {isWaypointsOpen || isBluecoinsOpen ? (
        <div className="vehicle-cluster">{props.items}</div>
      ) : (
        <CmdTooltip content={getVehiclesInCluster} backgroundColor="#252526">
          <div className="vehicle-cluster">{props.items}</div>
        </CmdTooltip>
      )}
    </div>
  );
}
